import { useState } from "react";
import { AlertTriangle, Ambulance, CheckCircle, BellRing, HeartPulse } from "lucide-react";
import { UnifiedRecord } from "../../../types";
import { useGlobalAlerts } from "../../../contexts/AlertContext";
import { EarlyWarningSystem } from "../../Dashboard/EarlyWarningSystem";

interface Props {
  records: UnifiedRecord[];
}

export function AlertsDashboard({ records }: Props) {
  const { addAlert, activeAlerts, removeAlert } = useGlobalAlerts();
  
  // Ambulance Simulation State
  const [ambulances, setAmbulances] = useState<{ id: string; type: string; eta: string; status: 'inbound' | 'arrived' }[]>([]);

  const simulateAmbulance = () => {
    const id = "AMB-" + Math.floor(Math.random() * 9000 + 1000);
    const types = ["Cardiac Arrest", "Severe Trauma", "Stroke Suspected", "Respiratory Failure"];
    const traumaType = types[Math.floor(Math.random() * types.length)];
    const etaMins = Math.floor(Math.random() * 8 + 2);
    
    setAmbulances(prev => [...prev, { id, type: traumaType, eta: `${etaMins} mins`, status: 'inbound' }]);
    
    addAlert({
      type: 'ambulance',
      title: 'Incoming Ambulance Alert',
      message: `Code 3: ${traumaType} inbound. Prepare trauma bay.`,
      metadata: { eta: `${etaMins} mins`, code: id }
    });
  };

  const markArrived = (id: string) => {
    setAmbulances(prev => prev.map(a => a.id === id ? { ...a, status: 'arrived' } : a));
    // Also remove the corresponding alert if it exists
    const alertToRemove = activeAlerts.find(a => a.type === 'ambulance' && a.metadata?.code === id);
    if (alertToRemove) {
      removeAlert(alertToRemove.id);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto pb-12">
      
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/5 pb-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center border border-red-500/30 shadow-[0_0_15px_rgba(239,68,68,0.3)]">
            <BellRing className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white tracking-wide">Emergency & Alerts</h2>
            <p className="text-gray-400 text-sm">Live monitoring of incoming ambulances and critical patient alerts.</p>
          </div>
        </div>
        <button 
          onClick={simulateAmbulance}
          className="flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white font-bold py-2.5 px-5 rounded-xl transition-all shadow-[0_0_15px_rgba(245,158,11,0.3)] hover:shadow-[0_0_25px_rgba(245,158,11,0.5)]"
        >
          <Ambulance className="w-5 h-5 animate-pulse" /> Simulate Inbound Ambulance
        </button>
      </div>

      {/* Ambulance Monitoring Section */}
      <div className="bg-[#040d1a]/80 backdrop-blur-xl border border-white/10 rounded-3xl p-6 shadow-xl">
        <h3 className="text-lg font-bold text-amber-400 flex items-center gap-2 mb-6">
          <AlertTriangle className="w-5 h-5" /> Active Inbound Ambulances
        </h3>
        
        {ambulances.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-500">
            <Ambulance className="w-12 h-12 mb-3 opacity-20" />
            <p>No active ambulances inbound at this time.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {ambulances.map(amb => (
              <div 
                key={amb.id}
                className={`p-5 rounded-2xl border ${
                  amb.status === 'inbound' 
                    ? 'bg-amber-500/10 border-amber-500/30 shadow-[0_0_15px_rgba(245,158,11,0.1)]' 
                    : 'bg-emerald-500/10 border-emerald-500/30'
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      amb.status === 'inbound' ? 'bg-amber-500/20 text-amber-400 animate-pulse-slow' : 'bg-emerald-500/20 text-emerald-400'
                    }`}>
                      <Ambulance className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-400 font-mono">{amb.id}</div>
                      <div className={`font-bold ${amb.status === 'inbound' ? 'text-amber-400' : 'text-emerald-400'}`}>
                        {amb.type}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider">ETA</div>
                    <div className="text-lg font-bold text-white">{amb.status === 'inbound' ? amb.eta : 'Arrived'}</div>
                  </div>
                  {amb.status === 'inbound' && (
                    <button 
                      onClick={() => markArrived(amb.id)}
                      className="bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-400 border border-emerald-500/30 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                    >
                      <CheckCircle className="w-4 h-4" /> Mark Arrived
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Patient Alerts (EWS) Section */}
      <div className="bg-[#040d1a]/80 backdrop-blur-xl border border-white/10 rounded-3xl p-6 shadow-xl">
        <h3 className="text-lg font-bold text-red-400 flex items-center gap-2 mb-6">
          <HeartPulse className="w-5 h-5" /> Critical Patient Monitoring
        </h3>
        
        {/* We reuse the EarlyWarningSystem component which already handles analyzing patient stats */}
        <EarlyWarningSystem records={records} />
        
      </div>
    </div>
  );
}
